<template>
  <div class="stock-kdj">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H7M7 12L11 8M11 12L15 16M15 12L19 8M19 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
            <path d="M12 2V4M12 20V22M2 12H4M20 12H22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h1>KDJ指标</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">随机指标，识别超买超卖与金叉死叉</p>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="searchForm.code"
        placeholder="请选择股票"
        filterable
        clearable
        @change="handleStockChange"
        class="stock-select"
        v-loading="stockListLoading"
      >
        <el-option
          v-for="item in stockList"
          :key="item.stock_code"
          :label="`${item.stock_name} (${item.stock_code})`"
          :value="item.stock_code"
        >
        </el-option>
      </el-select>

      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="开始日期"
        value-format="yyyy-MM-dd"
        @change="fetchData"
      />
      <span>至</span>
      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="结束日期"
        value-format="yyyy-MM-dd"
        @change="fetchData"
      />
      <el-button type="primary" @click="fetchData">查询</el-button>
    </div>

    <div ref="kdjChart" class="kdj-chart" v-loading="loading"></div>

    <!-- 数据表格 -->
    <div v-if="kdjData.length > 0" class="table-section">
      <div class="table-card">
        <el-table :data="kdjData" stripe class="kdj-table" max-height="400">
          <el-table-column prop="date" label="日期" width="120" />
          <el-table-column prop="close" label="收盘价" width="100" align="right">
            <template #default="{ row }">
              {{ row.close.toFixed(2) }}
            </template>
          </el-table-column>
          <el-table-column prop="k" label="K值" width="100" align="right">
            <template #default="{ row }">
              <span :class="getKDJValueClass(row.k)">
                {{ row.k.toFixed(2) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="d" label="D值" width="100" align="right">
            <template #default="{ row }">
              <span :class="getKDJValueClass(row.d)">
                {{ row.d.toFixed(2) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="j" label="J值" width="100" align="right">
            <template #default="{ row }">
              <span :class="getJValueClass(row.j)">
                {{ row.j.toFixed(2) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="rsv" label="RSV" width="100" align="right">
            <template #default="{ row }">
              {{ row.rsv.toFixed(2) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!loading && !latestStatus" class="empty-state">
      <el-empty description="暂无数据，请选择股票代码查询" />
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'StockKDJ',
  data() {
    return {
      searchForm: {
        code: ''
      },
      startDate: this.formatDate(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)),
      endDate: this.formatDate(new Date()),
      stockList: [],
      stockListLoading: false,
      kdjData: [],
      latestStatus: null,
      chart: null,
      loading: false,
      currentStockName: '',
      storageKey: 'selected_stock_code'
    }
  },
  watch: {
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode) {
          const selectedStock = this.stockList.find(item => item.stock_code === newCode)
          if (selectedStock) {
            this.currentStockName = selectedStock.stock_name
          }
          if (newCode !== this.searchForm.code) {
            this.searchForm.code = newCode
            localStorage.setItem(this.storageKey, newCode)
            this.fetchStockList()
            this.fetchDataWithCode(newCode)
          }
        }
      }
    },
    'searchForm.code'(newVal) {
      if (newVal) {
        localStorage.setItem(this.storageKey, newVal)
      }
    }
  },
  mounted() {
    this.initChart()
    this.restoreSelectedCode()
    if (!this.$route.query.code && !this.searchForm.code) {
      this.searchForm.code = 'sz.002602'
    }
    if (this.searchForm.code) {
      this.fetchStockList()
      this.fetchData()
    }
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.kdjChart)
      const option = this.getChartOption()
      this.chart.setOption(option)
    },

    async fetchStockList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockList = response.data.data
          // 更新当前股票名称
          if (this.searchForm.code) {
            const selectedStock = this.stockList.find(item => item.stock_code === this.searchForm.code)
            if (selectedStock) {
              this.currentStockName = selectedStock.stock_name
            }
          }
        } else {
          this.stockList = []
        }
      } catch (error) {
        console.error('获取股票列表失败:', error)
        this.$message.error('获取股票列表失败')
        this.stockList = []
      } finally {
        this.stockListLoading = false
      }
    },

    restoreSelectedCode() {
      const savedCode = localStorage.getItem(this.storageKey)
      if (savedCode) {
        this.searchForm.code = savedCode
      }
    },

    handleStockChange(code) {
      this.$router.push({ query: { code } })
    },

    async fetchDataWithCode(code) {
      this.loading = true
      try {
        const params = {
          start_date: this.startDate,
          end_date: this.endDate
        }

        const response = await axios.get(`/stock/kdj/${code}`, { params })

        if (response.data.status === 'success' && response.data.data) {
          this.kdjData = response.data.data
          if (this.kdjData.length > 0) {
            this.latestStatus = this.kdjData[this.kdjData.length - 1]
            this.updateChart()
          }
        } else {
          this.$message.error('返回状态异常: ' + (response.data.message || '未知错误'))
        }
      } catch (error) {
        console.error('获取KDJ数据失败:', error)
        this.$message.error('获取KDJ数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    async fetchData() {
      this.fetchDataWithCode(this.searchForm.code)
    },

    updateChart() {
      if (!this.chart || this.kdjData.length === 0) return

      const dates = this.kdjData.map(item => {
        const parts = item.date.split('-')
        return `${parts[1]}/${parts[2]}`
      })
      const kData = this.kdjData.map(item => item.k != null ? parseFloat(item.k) : null)
      const dData = this.kdjData.map(item => item.d != null ? parseFloat(item.d) : null)
      const jData = this.kdjData.map(item => item.j != null ? parseFloat(item.j) : null)
      const closeData = this.kdjData.map(item => item.close != null ? parseFloat(item.close) : null)

      const option = {
        title: {
          text: `${this.currentStockName || this.searchForm.code} KDJ指标`,
          left: 'center',
          textStyle: {
            color: '#cbd5e1',
            fontSize: 16
          }
        },
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          textStyle: {
            color: '#e2e8f0'
          },
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['K', 'D', 'J', '收盘价'],
          textStyle: {
            color: '#94a3b8'
          },
          top: 30
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '15%',
          bottom: '12%'
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: 'rgba(148, 163, 184, 0.3)'
            }
          },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11
          }
        },
        yAxis: [
          {
            name: 'KDJ值',
            nameTextStyle: {
              color: '#94a3b8'
            },
            position: 'left',
            axisLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.3)'
              }
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.1)'
              }
            },
            axisLabel: {
              color: '#94a3b8'
            }
          },
          {
            name: '收盘价',
            nameTextStyle: {
              color: '#94a3b8'
            },
            position: 'right',
            axisLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.3)'
              }
            },
            splitLine: {
              show: false
            },
            axisLabel: {
              color: '#94a3b8'
            }
          }
        ],
        series: [
          {
            name: 'K',
            type: 'line',
            data: kData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 0,
            lineStyle: {
              width: 2,
              color: '#fbbf24'
            },
            itemStyle: {
              color: '#fbbf24'
            }
          },
          {
            name: 'D',
            type: 'line',
            data: dData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 0,
            lineStyle: {
              width: 2,
              color: '#8b5cf6'
            },
            itemStyle: {
              color: '#8b5cf6'
            }
          },
          {
            name: 'J',
            type: 'line',
            data: jData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            yAxisIndex: 0,
            lineStyle: {
              width: 2,
              color: '#06b6d4'
            },
            itemStyle: {
              color: '#06b6d4'
            }
          },
          {
            name: '收盘价',
            type: 'line',
            data: closeData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            yAxisIndex: 1,
            lineStyle: {
              width: 1.5,
              color: '#22c55e'
            },
            itemStyle: {
              color: '#22c55e'
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    getKDJValueClass(value) {
      if (value > 80) return 'value-overbought'
      if (value < 20) return 'value-oversold'
      return 'value-normal'
    },

    getJValueClass(value) {
      if (value > 100) return 'value-overbought'
      if (value < 0) return 'value-oversold'
      return 'value-normal'
    },

    getSignalClass() {
      if (!this.latestStatus) return ''
      const k = this.latestStatus.k
      const d = this.latestStatus.d
      const j = this.latestStatus.j

      // 超买区：K>80 或 D>80
      if (k > 80 || d > 80) return 'signal-sell'
      // 超卖区：K<20 或 D<20
      if (k < 20 || d < 20) return 'signal-buy'
      // 金叉：K上穿D
      if (this.kdjData.length > 1) {
        const prev = this.kdjData[this.kdjData.length - 2]
        if (prev.k <= prev.d && k > d) return 'signal-buy'
        if (prev.k >= prev.d && k < d) return 'signal-sell'
      }
      return 'signal-neutral'
    },

    getSignalText() {
      if (!this.latestStatus) return '-'
      const k = this.latestStatus.k
      const d = this.latestStatus.d

      // 超买区
      if (k > 80 || d > 80) return '超买'
      // 超卖区
      if (k < 20 || d < 20) return '超卖'
      // 金叉死叉
      if (this.kdjData.length > 1) {
        const prev = this.kdjData[this.kdjData.length - 2]
        if (prev.k <= prev.d && k > d) return '金叉(买入)'
        if (prev.k >= prev.d && k < d) return '死叉(卖出)'
      }
      return '中性'
    },

    formatValue(value) {
      if (value === null || value === undefined) return '-'
      return parseFloat(value).toFixed(2)
    },

    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },

    getChartOption() {
      return {
        title: {
          text: '加载中...',
          left: 'center'
        }
      }
    }
  }
}
</script>

<style scoped>
.stock-kdj {
  padding: 20px;
  min-height: calc(100vh - 104px);
  position: relative;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 50%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #6366f1 50%, transparent 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
}

.stock-select {
  width: 280px;
}

.stock-select :deep(.el-input__inner),
.filter-bar :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stock-select :deep(.el-input__inner):hover,
.filter-bar :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4);
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15);
}

.stock-select :deep(.el-input__inner):focus,
.filter-bar :deep(.el-date-picker .el-input__inner):focus {
  border-color: #6366f1;
  box-shadow: 0 0 16px rgba(99, 102, 241, 0.25);
}

.filter-bar :deep(.el-date-picker .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6);
}

.filter-bar span {
  color: #64748b;
  font-size: 14px;
}

.filter-bar :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-button--primary):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
}

.filter-bar :deep(.el-button--primary):active {
  transform: translateY(0);
}

.kdj-chart {
  width: 100%;
  height: 500px;
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px;
  position: relative;
  z-index: 1;
  margin-bottom: 24px;
}

/* 表格区域 */
.table-section {
  margin-bottom: 0;
}

.table-card {
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px;
  position: relative;
  z-index: 1;
}

.kdj-table {
  width: 100%;
}

.kdj-table :deep(.el-table__header-wrapper) {
  background: rgba(15, 23, 42, 0.5);
}

.kdj-table :deep(th.el-table__cell) {
  background: rgba(15, 23, 42, 0.8) !important;
  color: #a5b4fc;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.kdj-table :deep(td.el-table__cell) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.kdj-table :deep(.el-table__row) {
  background: rgba(30, 41, 59, 0.3);
}

.kdj-table :deep(.el-table__row:hover td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

/* 空状态 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.empty-state :deep(.el-empty) {
  --el-empty-description-color: #94a3b8;
}
</style>
